import type { AgentRole, AgentRunPhase, RuntimeAgentRun } from "@private-fiction/story-core";

export interface AgentCheckpointRequest {
  runId: string;
  expectedRevision: number;
  experienceId: string;
  sessionId?: string;
  role: AgentRole;
  requestSummary: string;
  phase: AgentRunPhase;
  sourceMessageIds: string[];
  profile: Record<string, unknown>;
  contextManifest: Record<string, unknown>;
  checkpoint: Record<string, unknown>;
}

export interface AgentCheckpointReceipt { runId: string; phase: AgentRunPhase; revision: number; updatedAt: string; }

export type BackgroundWorkerRole = "retrieval" | "research" | "compiler" | "world_sync" | "memory" | "image" | "index";
export interface BackgroundJob {
  jobId: string; runId: string; experienceId: string; sessionId: string | null; workerRole: BackgroundWorkerRole;
  status: "queued" | "running" | "completed" | "failed" | "cancelled"; payload: Record<string, unknown>;
  result: Record<string, unknown> | null; checkpoint: Record<string, unknown>; attempt: number; maxAttempts: number;
  leaseOwner: string | null; leaseExpiresAt: string | null; error: string | null; createdAt: string | null; updatedAt: string | null;
}

export class AgentRepository {
  constructor(readonly endpoint: string, readonly accessToken: string) {}

  private async request(path: string, init: RequestInit = {}) {
    const response = await fetch(`${this.endpoint.replace(/\/$/, "")}${path}`, { ...init, headers: { "content-type": "application/json", authorization: `Bearer ${this.accessToken}`, ...init.headers } });
    const body = await response.json().catch(() => ({})) as Record<string, unknown>;
    if (!response.ok) throw new Error(String(body.error || `Agent Runtime returned ${response.status}`));
    return body;
  }

  async checkpoint(value: AgentCheckpointRequest):Promise<AgentCheckpointReceipt> {
    try {return await this.request("/v1/agent/runs/checkpoint", { method: "POST", body: JSON.stringify(value) }) as unknown as AgentCheckpointReceipt;}
    catch(error) {
      // Read back an uncertain commit; do not blindly retry a CAS write. A
      // concurrent client's different checkpoint must remain a conflict.
      try {
        const saved=await this.get(value.runId);
        const row=saved.run as unknown as {revision:number;phase:AgentRunPhase;session_id:string|null;experience_id:string;agent_role:string;checkpoint:Record<string,unknown>;updated_at:string};
        if(row && row.revision===value.expectedRevision+1 && row.phase===value.phase && row.session_id===(value.sessionId||null) && row.experience_id===value.experienceId && row.agent_role===value.role && Object.entries(value.checkpoint).filter(([key])=>key!=='_rolls').every(([key,item])=>stableJson(item)===stableJson(row.checkpoint[key]))) return {runId:value.runId,phase:row.phase,revision:row.revision,updatedAt:row.updated_at};
      } catch {/* Preserve the original write failure. */}
      throw error;
    }
  }
  async confirmTurnOutcome(runId:string,expectedRevision:number,outcome:import('@private-fiction/story-core').CompactTurnOutcome) {
    try {return await this.request('/v1/agent/turn-outcomes/confirm',{method:'POST',body:JSON.stringify({runId,expectedRevision,outcome})});}
    catch(error) {
      // A response may be lost after the atomic confirmation. Read the existing
      // job and accept only exactly the same immutable outcome, never a guess.
      try {
        const saved=await this.listJobs(runId);
        const job=saved.jobs.find(item=>item.payload.kind==='turn_compact_sync_v1' && stableJson(item.payload.outcome)===stableJson(outcome));
        if(job)return {jobId:job.jobId,runId,confirmed:true,status:job.status};
      }catch{/* Keep the original failure and the recoverable decision. */}
      throw error;
    }
  }
  turnRoll(runId:string,value:{action:'request'|'resolve';actorKey:string;callId:string;request:Record<string,unknown>}) {
    return this.request(`/v1/agent/runs/${encodeURIComponent(runId)}/roll`,{method:'POST',body:JSON.stringify(value)}) as Promise<unknown> as Promise<{revision:number;roll:import('./turnRecovery').PendingPlayerRoll}>;
  }

  get(runId: string) { return this.request(`/v1/agent/runs/${encodeURIComponent(runId)}`) as Promise<unknown> as Promise<{ run: RuntimeAgentRun; actions: unknown[]; toolCalls: unknown[] }>; }
  pending(sessionId:string) {return this.request(`/v1/agent/runs/pending?sessionId=${encodeURIComponent(sessionId)}`) as Promise<unknown> as Promise<{run:{runId:string;phase:string;revision:number}|null}>;}
  approve(runId: string, callId: string) { return this.request(`/v1/agent/runs/${encodeURIComponent(runId)}/approve/${encodeURIComponent(callId)}`, { method: "POST", body: "{}" }); }

  enqueueJob(value: { experienceId: string; sessionId: string; runId: string; workerRole: BackgroundWorkerRole; payload: Record<string, unknown>; maxAttempts?: number; idempotencyKey: string; priority?: number }) {
    return this.request("/v1/agent/jobs/enqueue", { method: "POST", body: JSON.stringify(value) }) as Promise<unknown> as Promise<{ jobId: string; runId: string; status: BackgroundJob["status"]; attempt: number; createdAt: string }>;
  }

  queueLocationImages(value: {storyId:string;fileId:string;runId:string;prompt:string;model:string;expectedRevision:number}) {
    return this.request("/v1/agent/location-images", {method:"POST",body:JSON.stringify(value)});
  }
  queueSessionMapImage(value: Record<string, unknown>) {
    return this.request("/v1/agent/maps/image", { method: "POST", body: JSON.stringify(value) });
  }
  queueCreatorSurfaceImage(value: Record<string, unknown>) {
    return this.request("/v1/creator/surfaces/image", { method: "POST", body: JSON.stringify(value) }) as Promise<unknown> as Promise<{ jobId: string; runId: string; status: "queued" | "running" }>;
  }
  queueCreatorFileImage(value: { referenceAssetIds?: string[]; imageTarget?: "cover" | "blueprint"; experienceId: string; fileId: string; runId: string; prompt: string; model: "sana-sprint-1.6b" | "sana" | "gpt-image-2" | "gpt-image-2.5-flare" | "gpt-image-2.5-sunburst" | "grok" | "grok-imagine" | string; aspectRatio: "1:1" | "4:3" | "3:4" | "16:9" | "9:16"; width: number; height: number }) {
    return this.request("/v1/creator/files/image", { method: "POST", body: JSON.stringify(value) }) as Promise<unknown> as Promise<{ jobId: string; runId: string; status: BackgroundJob["status"]; fileId: string; revision: number; createdAt: string }>;
  }

  leaseJobs(value: { runId: string; leaseOwner: string; workerRoles?: BackgroundWorkerRole[]; limit?: number; leaseSeconds?: number }) {
    return this.request("/v1/agent/jobs/lease", { method: "POST", body: JSON.stringify(value) }) as Promise<unknown> as Promise<{ jobs: BackgroundJob[] }>;
  }

  listJobs(runId: string) {
    return this.request(`/v1/agent/jobs?runId=${encodeURIComponent(runId)}`) as Promise<unknown> as Promise<{ jobs: BackgroundJob[] }>;
  }
  listSessionJobs(sessionId:string) {
    return this.request(`/v1/agent/jobs/session?sessionId=${encodeURIComponent(sessionId)}`) as Promise<unknown> as Promise<{jobs:BackgroundJob[]}>;
  }
  retryRuntimeJob(job:BackgroundJob) {
    return this.request(`/v1/agent/jobs/${encodeURIComponent(job.jobId)}/retry`,{method:'POST',body:JSON.stringify({expectedUpdatedAt:job.updatedAt})}) as Promise<unknown> as Promise<{job:BackgroundJob}>;
  }

  heartbeatJob(jobId: string, leaseOwner: string, leaseSeconds = 120) {
    return this.request(`/v1/agent/jobs/${encodeURIComponent(jobId)}/heartbeat`, { method: "POST", body: JSON.stringify({ leaseOwner, leaseSeconds }) });
  }

  completeJob(jobId: string, leaseOwner: string, result: Record<string, unknown>, checkpoint: Record<string, unknown> = {}) {
    return this.request(`/v1/agent/jobs/${encodeURIComponent(jobId)}/complete`, { method: "POST", body: JSON.stringify({ leaseOwner, result, checkpoint }) });
  }

  failJob(jobId: string, leaseOwner: string, error: string, checkpoint: Record<string, unknown> = {}, retryDelaySeconds = 5) {
    return this.request(`/v1/agent/jobs/${encodeURIComponent(jobId)}/fail`, { method: "POST", body: JSON.stringify({ leaseOwner, error, checkpoint, retryDelaySeconds }) });
  }
}

function stableJson(value:unknown):string|undefined {
  return JSON.stringify(value,(_key,item)=>item&&typeof item==='object'&&!Array.isArray(item)?Object.fromEntries(Object.keys(item).sort().map(key=>[key,item[key]])):item);
}
